/**
 * @fliisi/pollen — Design Tokens
 *
 * Raw design values: spacing, sizing, radii, and color primitives.
 * These are the building blocks for themes.
 *
 * Usage in components:
 *   <Box p="$md" br="$lg" bg="$colorPrimary" />
 */
import { createTokens } from 'tamagui'

// ─── Color Palette ───────────────────────────────────────────────────────

export const palette = {
  // Garden theme (sage green - buyer/seller)
  garden50: '#F4F7F4',
  garden100: '#E8EFE8',
  garden200: '#C5D9C5',
  garden300: '#A3C3A3',
  garden400: '#748C70', // Primary garden
  garden500: '#5A7A58',
  garden600: '#4A6848',
  garden700: '#3A5638',
  garden800: '#2A4428',
  garden900: '#1A3218',

  // Brick theme (terracotta - merchant/store)
  brick50: '#FBF5F3',
  brick100: '#F7EBE7',
  brick200: '#E8D0C6',
  brick300: '#D9B5A5',
  brick400: '#C78F7C', // Primary brick
  brick500: '#B87460',
  brick600: '#A35E4A',
  brick700: '#8E4834',
  brick800: '#79321E',
  brick900: '#641C08',

  // Neutrals (warm-shifted to match the earthy Fliisi aesthetic)
  white: '#FFFFFF',
  black: '#0A0A0A',
  gray50: '#FAFAFA',
  gray100: '#F5F5F5',
  gray200: '#E0E2DB',  // Light Stone (live: ui.border)
  gray300: '#D4D4D4',
  gray400: '#858B80',  // Muted Sage Grey (live: text.secondary)
  gray500: '#6B7066',
  gray600: '#525252',
  gray700: '#404040',
  gray800: '#3E423A',  // Dark Olive (live: text.primary)
  gray900: '#3E423A',  // Same — slightly less black than pure dark

  // Semantic (earthy, muted — matches Fliisi brand)
  success: '#748C70',  // Sage green (brand primary, not neon)
  warning: '#E8B49C',  // Soft clay
  error: '#D32F2F',    // Muted red
  info: '#5D7C89',     // Muted blue-grey
}

// ─── Tokens ──────────────────────────────────────────────────────────────

export const tokens = createTokens({
  // Spacing scale (use for margin, padding, gap)
  space: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 20,
    xl: 24,
    '2xl': 32,
    '3xl': 48,
    '4xl': 64,
    '5xl': 96,
    true: 16, // Default when using $space
  },

  // Sizing scale (use for width, height, icon sizes)
  size: {
    xs: 16,
    sm: 24,
    md: 32,
    lg: 40,
    xl: 48,
    '2xl': 64,
    '3xl': 96,
    '4xl': 128,
    '5xl': 192,
    // Screen layout max-widths
    screenNarrow: 480,
    screenMd: 768,
    screenWide: 1024,
    true: 32, // Default when using $size
  },

  // Border radii (matches live app theme)
  radius: {
    sm: 6,
    md: 8,
    lg: 12,
    xl: 16,
    '2xl': 24,
    full: 999,
    true: 8, // Default
  },

  // Z-index scale
  // xs=0, sm=10, md=20, lg=30, xl=40, 2xl=50, 3xl=60, 4xl=70
  zIndex: {
    xs: 0,
    sm: 10,
    md: 20,
    lg: 30,
    xl: 40,
    '2xl': 50,
    '3xl': 60,
    '4xl': 70,
    true: 0, // Default
  },

  // Colors (raw palette - themes map these semantically)
  color: palette,
})
